import type {
  StreamEvent,
  StreamResult,
  StartArgs,
} from './Types';
import type { Transport } from './transports';
import { streamLog } from './streamLogger';

export type OrchestratorStartOptions = {
  primary: Transport;
  onEvent: (e: StreamEvent) => void;
  args: StartArgs;
};

export type OrchestratorRun = {
  cancel: () => void;
  done: Promise<StreamResult>;
};

export function startStream(options: OrchestratorStartOptions): OrchestratorRun {
  const { primary, onEvent, args } = options;

  let accumulated = '';
  let cancelled = false;
  

  let currentCancel: (() => void) | null = null;
  let resolveRun: (r: StreamResult) => void;
  let rejectRun: (e: any) => void;
  const done = new Promise<StreamResult>((resolve, reject) => {
    resolveRun = resolve;
    rejectRun = reject;
  });

  const startTime = args.startTimeMs;

  const runTransport = (transport: Transport) => {
    const transportResult = transport(args, (e: StreamEvent) => {
      if (cancelled) return;
      if (e.type === 'text_delta' && typeof e.delta === 'string') {
        accumulated += e.delta;
      }
      if (e.type === 'complete') {
        // If transport provided final content, prefer it
        if (typeof e.content === 'string' && e.content.length > 0) {
          accumulated = e.content;
        }
      }
      onEvent(e);
    });
    
    const { cancel, done, capture } = transportResult;
    currentCancel = cancel;

    done
      .then(() => {
        const timeMs = Date.now() - startTime;
        
        // Finalize capture with accumulated content if available
        if (capture && typeof capture.finalize === 'function') {
          try {
            capture.finalize(accumulated);
          } catch (captureError) {
            try { streamLog.error('🚨 Stream capture finalization error (non-blocking):', captureError); } catch (_) {}
          }
        }
        
        resolveRun({ raw: accumulated, timeMs });
      })
      .catch((error) => {
        // Finalize capture with error if available
        if (capture && typeof capture.finalize === 'function') {
          try {
            capture.captureError(`Orchestrator error: ${error instanceof Error ? error.message : String(error)}`);
            capture.finalize(accumulated);
          } catch (captureError) {
            try { streamLog.error('🚨 Stream capture error finalization error (non-blocking):', captureError); } catch (_) {}
          }
        }
        rejectRun(error);
      });
  };

  runTransport(primary);

  return {
    cancel: () => {
      cancelled = true;
      currentCancel?.();
    },
    done,
  };
}


